<script setup lang="ts">
import { computed } from "vue";
import { stringToHslColor } from "@/services/Utils/StringToHSLColor";

const props = defineProps<{
  label: string;
  id?: string;
  canBeDeleted?: boolean;
  color: "primary" | "positive" | "warning" | "danger" | "custom" | "label" | "neutral";
}>();

const emit = defineEmits<{
  (e: "onDelete", value: string): void;
}>();

const deleteTag = () => {
  emit("onDelete", props.id ?? props.label);
};

const colorClass = computed<string>(() => {
  if (props.color === "primary") return "bg-app-blue-600 dark:bg-app-blue-fill text-white";
  if (props.color === "positive") return "bg-app-green-700 dark:bg-app-green-fill text-white";
  // For metadata that carries no status: a file format, a unit, a count. The semantic colours all
  // read as a verdict, so using one of them to label ".xlsx" says "success" about a file extension.
  if (props.color === "neutral") return "bg-slate-100 text-type-muted";
  if (props.color === "warning") return "bg-app-yellow-700 dark:bg-app-yellow-fill text-white";
  if (props.color === "custom") return "bg-custom-state";
  if (props.color === "label") return "";
  return "bg-app-red-600 dark:bg-app-red-fill text-white";
});

// Per-label hue computed at runtime, so it travels as CSS variables; the dark value is lighter to stay readable.
const labelStyle = computed<string | undefined>(() => {
  if (props.color !== "label") return undefined;
  return `--tag-hue-light: ${stringToHslColor(props.label, 50, 40)}); --tag-hue-dark: ${stringToHslColor(props.label, 60, 75)})`;
});
</script>

<template>
  <div
    class="flex h-fit w-fit items-center gap-2 rounded-full p-1 px-2 text-xs leading-none"
    :class="[colorClass, { 'tag-label': color === 'label' }]"
    :style="labelStyle">
    <span class="font-semibold">{{ label }}</span>
    <!--
      Hover emphasis is opacity, not a colour change. Red said "destructive" for what is just
      removing a tag, and it landed as red-on-blue inside a solid app-blue chip. Opacity also works
      on every colour this component takes, including the light `neutral` and per-hue `label` fills,
      where a fixed hover colour would have to be picked per variant.
    -->
    <button
      v-if="canBeDeleted"
      type="button"
      :aria-label="`Remove ${label}`"
      class="duration-fast ease-standard cursor-pointer opacity-70 transition-opacity hover:opacity-100"
      @click="deleteTag">
      <font-awesome-icon
        icon="fa-solid fa-x"
        class="h-2 w-2" />
    </button>
  </div>
</template>

<style scoped>
.bg-custom-state {
  background: rgba(153, 106, 51, 0.22);
  color: rgb(113, 78, 37);
}

html.dark .bg-custom-state {
  background: rgba(214, 166, 106, 0.2);
  color: #e0b98a;
}

.tag-label {
  background: color-mix(in srgb, var(--tag-hue-light) 22%, transparent);
  color: var(--tag-hue-light);
}

html.dark .tag-label {
  background: color-mix(in srgb, var(--tag-hue-dark) 22%, transparent);
  color: var(--tag-hue-dark);
}
</style>
